/**
 * Rules over `GET /api/account-security/status`: whether enrollment is forced,
 * how a method reads, which methods may be removed, and how a step-up runs.
 */

import type { LoginMfaMethod, LoginMfaMethodType, MethodStrength, MfaMethod, MfaMethodType, SecurityStatus } from './domain';

export interface EnrollmentState {
  /** No method yet while the org requires MFA (`account-security-enforcement`): the page shows only the enrollment flow. */
  required: boolean;
  needsMethod: boolean;
}

/** Enrollment is complete with one verified method. */
export function enrollmentState(status: Pick<SecurityStatus, 'mfa_required' | 'methods'>): EnrollmentState {
  const needsMethod = !status.methods || status.methods.length === 0;
  return { required: !!status.mfa_required && needsMethod, needsMethod };
}

export function nextEnrollmentStep(state: EnrollmentState): 'method' | 'done' {
  return state.needsMethod ? 'method' : 'done';
}

const TYPE_LABELS: Record<LoginMfaMethodType, string> = {
  totp: 'Authenticator app',
  webauthn: 'Passkey or security key',
  sms: 'Text message',
  voice: 'Phone call',
  backup_code: 'Backup code',
  session_approval: 'Approve on another device',
};

export function methodTypeLabel(type: LoginMfaMethodType | string): string {
  return (TYPE_LABELS as Record<string, string>)[type] ?? type;
}

/** The user's own label first; the phone number for SMS and voice; else the type. */
export function methodDisplayName(method: Pick<LoginMfaMethod, 'type' | 'label' | 'phone_masked'>): string {
  const secondary = methodSecondary(method);
  return secondary ? `${methodTypeLabel(method.type)} · ${secondary}` : methodTypeLabel(method.type);
}

/**
 * The small line under a method's title: the masked phone for text and call,
 * and the user's own nickname as "Named: …". Never derived from a user agent
 * or platform: sign-in is device- and vendor-agnostic, so the title is always
 * the method type (methodTypeLabel).
 */
export function methodSecondary(method: Pick<LoginMfaMethod, 'type' | 'label' | 'phone_masked'>): string {
  const parts: string[] = [];
  if ((method.type === 'sms' || method.type === 'voice') && method.phone_masked) parts.push(method.phone_masked);
  const label = (method.label || '').trim();
  if (label && label !== methodTypeLabel(method.type) && label !== method.phone_masked) parts.push(`Named: ${label}`);
  return parts.join(' · ');
}

export const METHOD_LABEL_MAX = 60;

/** A method nickname from the rename field: trimmed, 1-60 characters. */
export function validateMethodLabel(raw: string): { label: string } | { error: string } {
  const label = (raw || '').trim().replace(/\s+/g, ' ');
  if (!label) return { error: 'Enter a name, or cancel.' };
  if (label.length > METHOD_LABEL_MAX) return { error: `Use ${METHOD_LABEL_MAX} characters or fewer.` };
  return { label };
}

// ── "Added … · Last used …" ────────────────────────────────────────────────

/** A server timestamp as epoch ms: a bare `Y-m-d H:i:s` reads as local time (as the AngularJS date filter did); ISO keeps its offset. */
export function parseAccountTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const at = Date.parse(/^\d{4}-\d{2}-\d{2} \d/.test(value) ? value.replace(' ', 'T') : value);
  return isNaN(at) ? null : at;
}

export interface ActivityFormat {
  /** BCP 47 locale for dates (the user's), e.g. 'en-US'. */
  locale?: string;
  /** IANA time zone for dates (the user's). */
  timeZone?: string;
}

const DAY_MS = 86400000;

function absoluteDate(at: number, fmt: ActivityFormat): string {
  return new Intl.DateTimeFormat(fmt.locale || 'en-US', { month: 'short', day: 'numeric', year: 'numeric', ...(fmt.timeZone ? { timeZone: fmt.timeZone } : {}) }).format(at);
}

function fullDateTime(at: number, fmt: ActivityFormat): string {
  return new Intl.DateTimeFormat(fmt.locale || 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(fmt.timeZone ? { timeZone: fmt.timeZone } : {}),
  }).format(at);
}

/** "just now", "5 minutes ago", "2 hours ago", "yesterday", "3 days ago" under 7 days; the date after. */
export function relativeOrDate(at: number, now: number, fmt: ActivityFormat = {}): string {
  const diff = Math.max(0, now - at);
  if (diff >= 7 * DAY_MS) return absoluteDate(at, fmt);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

export interface ActivityLine {
  /** "Added Oct 3, 2026 · Last used 2 hours ago" */
  text: string;
  /** The same with full local date-times, for a tooltip. */
  title: string;
}

/** A method's or backup codes' dates: `Added`/`Created`, then "Last used …" or "Never used". */
export function activityLine(
  dates: { created_at?: string | null; last_used_at?: string | null },
  now: number,
  fmt: ActivityFormat = {},
  createdWord: 'Added' | 'Created' = 'Added',
): ActivityLine {
  const created = parseAccountTime(dates.created_at);
  const used = parseAccountTime(dates.last_used_at);
  const text: string[] = [];
  const title: string[] = [];
  if (created !== null) {
    text.push(`${createdWord} ${absoluteDate(created, fmt)}`);
    title.push(`${createdWord} ${fullDateTime(created, fmt)}`);
  }
  if (used !== null) {
    text.push(`Last used ${relativeOrDate(used, now, fmt)}`);
    title.push(`Last used ${fullDateTime(used, fmt)}`);
  } else {
    text.push('Never used');
    title.push('Never used');
  }
  return { text: text.join(' · '), title: title.join(' · ') };
}

export interface RemovalVerdict {
  allowed: boolean;
  reason?: string;
}

/**
 * Mirrors the server refusal (contract: "cannot remove last verified method
 * while mfa_required") so the button explains itself instead of failing.
 * The server check stays the enforcement.
 */
export function canRemoveMethod(status: Pick<SecurityStatus, 'mfa_required' | 'methods'>, methodUuid: string): RemovalVerdict {
  const methods = status.methods || [];
  if (!methods.some((method) => method.uuid === methodUuid)) return { allowed: false, reason: 'This method no longer exists.' };
  if (status.mfa_required && methods.length <= 1) {
    return { allowed: false, reason: 'Your organization requires two-step verification. Add another method before removing this one.' };
  }
  return { allowed: true };
}

/**
 * How a step-up (or the login second factor) proceeds for a method:
 *   `code`      type the current code (authenticator app)
 *   `send-code` the server sends a code first (SMS, voice), then type it
 *   `webauthn`  the server returns request options; the browser signs
 */
export type StepUpKind = 'code' | 'send-code' | 'webauthn' | 'session-approval';

export function stepUpKind(type: LoginMfaMethodType | string): StepUpKind {
  if (type === 'webauthn') return 'webauthn';
  if (type === 'session_approval') return 'session-approval';
  if (type === 'sms' || type === 'voice') return 'send-code';
  return 'code';
}

export function stepUpPrompt(method: Pick<LoginMfaMethod, 'type' | 'phone_masked'>): string {
  switch (stepUpKind(method.type)) {
    case 'session-approval':
      return 'Open Dealr on a device where you are already signed in, go to your account settings, and approve this sign-in.';
    case 'webauthn':
      return 'Use your security key or passkey when your browser asks.';
    case 'send-code':
      return method.type === 'voice'
        ? `We'll call ${method.phone_masked || 'your phone'} with a code.`
        : `We'll text a code to ${method.phone_masked || 'your phone'}.`;
    default:
      return method.type === 'backup_code'
        ? 'Enter one of your backup codes. Each code works only once.'
        : 'Enter the 6-digit code from your authenticator app.';
  }
}

/** A sensible first choice: the most recently used method, else the first. */
export function defaultMethod<T extends Pick<MfaMethod, 'uuid'> & { last_used_at?: string | null }>(methods: readonly T[]): T | null {
  if (!methods.length) return null;
  let best: T | null = null;
  for (const method of methods) {
    if (!method.last_used_at) continue;
    if (!best || String(method.last_used_at) > String(best.last_used_at)) best = method;
  }
  return best ?? methods[0] ?? null;
}

/** Whether the browser offers WebAuthn; the adapter passes what it found on its platform. */
export function webAuthnSupported(platform: { publicKeyCredential: unknown; credentials: unknown }): boolean {
  return !!platform.publicKeyCredential && !!platform.credentials;
}

// ── Backup codes (account-security) ───────────────────────────────────

/** The `method_uuid` the server accepts a backup code under (login, reset, step-up). */
export const BACKUP_CODE_METHOD_UUID = 'backup';

export function isBackupCodeMethod(method: Pick<LoginMfaMethod, 'type' | 'uuid'> | null | undefined): boolean {
  return !!method && (method.type === 'backup_code' || method.uuid === BACKUP_CODE_METHOD_UUID);
}

/** The pseudo-method a step-up offers when the account still has backup codes. */
export function backupCodeMethod(): MfaMethod & { type: MfaMethodType } {
  return {
    uuid: BACKUP_CODE_METHOD_UUID,
    type: 'backup_code' as unknown as MfaMethodType,
    label: 'Backup code',
    phone_masked: null,
    last_used_at: null,
    created_at: null,
  };
}

/** Remaining codes, or null when the server sent no `backup_codes` (flag off). */
export function backupCodesRemaining(status: Pick<SecurityStatus, 'backup_codes'> | null | undefined): number | null {
  const info = status && status.backup_codes;
  if (!info || typeof info !== 'object' || typeof info.remaining !== 'number') return null;
  return Math.max(0, info.remaining);
}

/** Codes are base32 shown as `xxxxx-xxxxx`; case, spaces and the dash never count. */
export function normalizeBackupCode(code: string): string {
  return (code || '').replace(/[\s-]/g, '').toUpperCase();
}

export function isValidBackupCode(code: string): boolean {
  return /^[A-Z2-7]{10}$/.test(normalizeBackupCode(code));
}

/** One code per line, for the copy, download and print actions. */
export function backupCodesText(codes: readonly string[], heading = 'Dealr backup codes'): string {
  return [heading, 'Each code can be used once.', '', ...codes].join('\n') + '\n';
}

// ── Method strength (account-security) ──────────────────────────

const DEFAULT_STRENGTH: Record<MfaMethodType, MethodStrength> = {
  webauthn: 'strong',
  totp: 'strong',
  sms: 'weak',
  voice: 'weak',
};

const STRENGTH_ORDER: Record<string, number> = { webauthn: 0, totp: 1, sms: 2, voice: 3, backup_code: 4 };

export type StrengthMap = Partial<Record<MfaMethodType, MethodStrength>> | null | undefined;

/** Whether the server sent strength ratings, i.e. `account-security` is on. */
export function hasStrengthInfo(strength: StrengthMap): boolean {
  return !!strength && typeof strength === 'object' && Object.keys(strength).length > 0;
}

export function methodStrength(type: LoginMfaMethodType | string, strength?: StrengthMap): MethodStrength {
  const fromServer = strength ? (strength as Record<string, MethodStrength | undefined>)[type] : undefined;
  if (fromServer === 'strong' || fromServer === 'weak') return fromServer;
  return (DEFAULT_STRENGTH as Record<string, MethodStrength>)[type] ?? 'weak';
}

/** Strongest first (security key, authenticator, text, call); stable within a type. */
export function sortMethodsByStrength<T extends { type: string }>(methods: readonly T[], strength?: StrengthMap): T[] {
  const rank = (m: T) => (methodStrength(m.type, strength) === 'strong' ? 0 : 10) + (STRENGTH_ORDER[m.type] ?? 5);
  return methods
    .map((method, index) => ({ method, index }))
    .sort((a, b) => rank(a.method) - rank(b.method) || a.index - b.index)
    .map((entry) => entry.method);
}

/** Enrolled methods split into what the page leads with and what it files under "Less secure options". */
export function groupMethodsByStrength<T extends { type: string }>(methods: readonly T[], strength?: StrengthMap): { strong: T[]; weak: T[] } {
  const sorted = sortMethodsByStrength(methods, strength);
  return {
    strong: sorted.filter((m) => methodStrength(m.type, strength) === 'strong'),
    weak: sorted.filter((m) => methodStrength(m.type, strength) === 'weak'),
  };
}

/** The strongest strong method, never a backup code: the one a picker can pre-select. */
export function strongestMethod<T extends { type: string }>(methods: readonly T[], strength?: StrengthMap): T | null {
  const candidates = methods.filter((m) => m.type !== 'backup_code' && methodStrength(m.type, strength) === 'strong');
  return sortMethodsByStrength(candidates, strength)[0] ?? null;
}

/** The nudge banner: the server says the user's only methods are SMS/voice. */
export function recommendUpgrade(status: Pick<SecurityStatus, 'recommend_upgrade'> | null | undefined): boolean {
  return !!status && status.recommend_upgrade === true;
}

export const SIM_SWAP_WARNING =
  'Text and call codes work anywhere, but anyone who takes over your phone number could get them too. A passkey or authenticator app is safer.';

/**
 * The methods a step-up offers: strongest first when the server rated them
 * (`account-security`), plus a backup code while any remain
 * (`account-security`). Each feature is on only with its flag.
 */
export function stepUpMethodList(
  status: Pick<SecurityStatus, 'methods' | 'method_strength' | 'backup_codes'> | null | undefined,
  features: { strengthRated: boolean; backupCodes: boolean },
): MfaMethod[] {
  const methods = (status && status.methods) || [];
  const list = features.strengthRated ? sortMethodsByStrength(methods, status!.method_strength) : methods.slice();
  if (features.backupCodes && (backupCodesRemaining(status) ?? 0) > 0) list.push(backupCodeMethod());
  return list;
}

/**
 * Which page `app.user.settings` renders. `release-account-settings-v2` owns the
 * island; `account-security-enforcement` alone forces it only for an account
 * that still owes MFA enrollment, because the legacy page has no enrollment UI.
 */
export function userSettingsSurface(flags: { settingsV2: boolean; enforcement: boolean; enrollmentRequired: boolean }): 'island' | 'legacy' {
  if (flags.settingsV2) return 'island';
  return flags.enforcement && flags.enrollmentRequired ? 'island' : 'legacy';
}

const RENAME_ERRORS: Record<string, string> = {
  invalid_label: 'Use 1 to 60 characters.',
  method_not_found: 'This method was already removed. Refresh the page to see your current list.',
  rate_limited: 'Too many changes in a row. Try again in a few minutes.',
};

/** The sentence for a failed method rename, from the backend `code` (or HTTP status when the body has none). */
export function renameErrorMessage(code: string | null | undefined, status = 0): string | null {
  if (code && RENAME_ERRORS[code]) return RENAME_ERRORS[code];
  if (status === 404) return RENAME_ERRORS.method_not_found ?? null;
  if (status === 429) return RENAME_ERRORS.rate_limited ?? null;
  return null;
}
